import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';

import css from './ListingTypeButtonFilter.module.css';

/**
 * ListingTypeButtonFilter component
 * Renders listing types as individual button filters
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The ID
 * @param {string} props.name - The name
 * @param {React.Node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Array<{option: string, label: string}>} props.options - The listing type options
 * @param {Function} props.onSubmit - The function to submit filter value
 * @param {Array<string>} [props.hiddenOptions] - Array of listing type options to hide (e.g., ['listing-services'])
 * @returns {JSX.Element}
 */
const ListingTypeButtonFilter = props => {
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    queryParamNames,
    initialValues,
    options,
    onSubmit,
    hiddenOptions = ['listing-services'],
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'pub_listingType';

  // Get the currently selected listing type from initial values
  const currentValue = initialValues?.[queryParamName];

  // Filter out hidden options
  const visibleOptions = options.filter(opt => !hiddenOptions.includes(opt.option));

  // Handle button click
  const handleButtonClick = optionValue => {
    // If clicking the same option, clear the filter (deselect)
    const newValue = currentValue === optionValue ? null : optionValue;
    onSubmit({ [queryParamName]: newValue });
  };

  return (
    <div className={classes}>
      <div className={css.filterHeader}>
        <span className={css.label}>{label}</span>
      </div>
      <div className={css.buttonsWrapper}>
        {visibleOptions.map(option => {
          const isSelected = currentValue === option.option;
          const buttonClasses = classNames(css.button, {
            [css.buttonSelected]: isSelected,
          });

          return (
            <button
              key={option.option}
              type="button"
              className={buttonClasses}
              onClick={() => handleButtonClick(option.option)}
              data-testid={`listing-type-button-${option.option}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ListingTypeButtonFilter;
